import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CUSTOMER_APPROVAL, shipmentDocTypes } from 'config/documents'; // Pull this from the document service once documents are retrieved from there
import { underscored } from 'underscore.string';
import { sortBy } from 'lodash';
import Label from 'components/v2/forms/Label';
import ToggleSwitch from 'components/v2/forms/ToggleSwitch';
import Button from 'components/v2/buttons/Button';
import AutocompleteSearch from 'components/v2/AutocompleteSearch';
import CargomaticModal from 'components/v2/modal';
import { upsertAccessorialRequestRule } from 'utils/UserServiceAPIUtils';
import { getAccessorials } from 'utils/AccessorialWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import MultiSelectTable from 'components/v2/MultiSelectTable';
import Alert from 'components/ui/Alert';
export default function AccessorialAddEditModal({ shipperId, accessorial, onHide, editMode, addMode, onSave, }) {
    const { t } = useTranslation();
    const sortDocs = (docs) => {
        return sortBy(docs, (x) => x === null || x === void 0 ? void 0 : x.name);
    };
    const SHIPMENT_DOCS_MAPPED = shipmentDocTypes.map((doc) => {
        return {
            name: t(doc.localizationSlug),
            value: doc.value,
        };
    }).filter((doc) => doc.value !== 'customer_approval');
    const [accessorialName, setAccessorialName] = useState();
    const [customerApprovalRequired, setCustomerApprovalRequired] = useState(false);
    const [requiredDocs, setRequiredDocs] = useState([]);
    const [allAccessorialsList, setAllAccessorialsList] = useState([]);
    useEffect(() => {
        const fetchAccessorials = async () => {
            try {
                const result = await getAccessorials({
                    paginate: false,
                });
                const { entity: { items }, } = result;
                const accessorials = items.map((a) => {
                    var _a;
                    return ({
                        name: a === null || a === void 0 ? void 0 : a.name,
                        value: (a === null || a === void 0 ? void 0 : a.slug) || underscored((_a = a === null || a === void 0 ? void 0 : a.name) === null || _a === void 0 ? void 0 : _a.toLowerCase()),
                    });
                });
                setAllAccessorialsList(accessorials);
            }
            catch (e) {
                console.error('error pulling accessorials');
            }
        };
        if (addMode) {
            fetchAccessorials();
        }
    }, [addMode]);
    useEffect(() => {
        var _a;
        if (accessorial && editMode) {
            setAccessorialName(accessorial.accessorial);
            setRequiredDocs(sortDocs((_a = accessorial.rules) === null || _a === void 0 ? void 0 : _a.map((doc) => {
                var _a;
                return { name: t((_a = doc === null || doc === void 0 ? void 0 : doc.value) === null || _a === void 0 ? void 0 : _a.toUpperCase()), value: doc === null || doc === void 0 ? void 0 : doc.value };
            })));
            setCustomerApprovalRequired(accessorial.customer_approval_required);
        }
    }, [accessorial]);
    const handleSave = async () => {
        var _a, _b, _c;
        try {
            const mappedRequiredDocs = requiredDocs.map((doc) => ({
                value: doc.value,
            }));
            const updatedAccessorialRequestRule = {
                customer_approval_required: customerApprovalRequired,
                accessorial: accessorialName,
                ruleSet: mappedRequiredDocs,
            };
            const response = await upsertAccessorialRequestRule(shipperId, 'shipper', [updatedAccessorialRequestRule]);
            if (!response) {
                throw new Error('No response from API');
            }
            const refreshAccessorial = (_c = (_b = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.upsertAccessorialRequestRule) === null || _b === void 0 ? void 0 : _b.items) === null || _c === void 0 ? void 0 : _c.find((acc) => acc.accessorial === accessorialName);
            onSave(refreshAccessorial);
            showToast(t('EDIT_ACCESSORIAL_REQUEST_SUCCESS'));
        }
        catch (err) {
            console.error('Error during upsertAccessorialRequestRule: ', err.message);
            showToast(t('EDIT_ACCESSORIAL_REQUEST_FAILURE'), null, 'error');
        }
    };
    const handleAccessorialSelect = (accessorial) => {
        setAccessorialName(accessorial.value);
    };
    const handleCustomerApprovalToggle = (isRequired) => {
        if (!isRequired) {
            const docs = requiredDocs.filter((doc) => doc.value !== CUSTOMER_APPROVAL.value);
            setRequiredDocs(docs);
        }
        setCustomerApprovalRequired(isRequired);
    };
    const renderEditAccessorialRequest = () => {
        if (!accessorial && editMode)
            return null;
        const renderSearchBox = () => {
            if (addMode) {
                return (React.createElement("div", { className: "mb-4" },
                    React.createElement(Label, { labelClassName: "block text-sm font-medium text-gray-700 font-semibold" }, t('ACCESSORIALS')),
                    React.createElement(AutocompleteSearch, { placeholder: t('SEARCH_ACCESSORIAL'), items: allAccessorialsList, searchKey: "name", onSelect: handleAccessorialSelect, nGramLength: 1 })));
            }
        };
        const renderShipperAccessorialInfoLabel = () => {
            if (addMode) {
                return (React.createElement(Alert, { type: "info", className: "mt-4" }, t('ADD_SHIPPER_ACCESSORIAL_INFO_NOTE')));
            }
        };
        return (React.createElement(React.Fragment, null,
            renderSearchBox(),
            accessorialName && (React.createElement("div", { className: "mb-4" },
                React.createElement(Label, { labelClassName: "block text-sm font-medium text-gray-700 font-semibold" }, t('ACCESSORIAL')),
                t(accessorialName === null || accessorialName === void 0 ? void 0 : accessorialName.toUpperCase()))),
            React.createElement("div", { className: "mb-4" },
                React.createElement(Label, { labelClassName: "block text-sm font-medium text-gray-700 font-semibold", helptext: t('CUSTOMER_APPROVAL_REQUIRED_TOOLTIP') }, t('CUSTOMER_APPROVAL_REQUIRED_QUESTION')),
                React.createElement(ToggleSwitch, { enabled: customerApprovalRequired, onChange: handleCustomerApprovalToggle })),
            React.createElement(MultiSelectTable, { selectedColumnName: t('REQUIRED_DOCUMENTS'), availableColumnName: t('AVAILABLE_DOCUMENTS'), columnNamesClassName: "block text-sm font-medium text-gray-700 font-semibold text-nowrap", selectedItems: requiredDocs, allItemsList: SHIPMENT_DOCS_MAPPED, onSelect: setRequiredDocs }),
            renderShipperAccessorialInfoLabel()));
    };
    const handleOnSave = () => {
        handleSave();
        onHide();
        setAccessorialName();
        setAccessorialName();
        setRequiredDocs([]);
        setCustomerApprovalRequired(false);
    };
    const handleOnCancel = () => {
        onHide();
        setAccessorialName();
        setRequiredDocs([]);
        setCustomerApprovalRequired(false);
    };
    const renderEditAccessorialRequestFooter = () => {
        const { t } = useTranslation();
        return (React.createElement("div", null,
            React.createElement(Button, { className: "m-1", color: "green", type: "button", onClick: handleOnSave, disabled: !accessorialName }, t('SAVE')),
            React.createElement(Button, { className: "m-1", type: "button", onClick: handleOnCancel }, t('CANCEL'))));
    };
    return editMode ? (React.createElement(CargomaticModal, { showCloseButton: false, title: t('EDIT_ACCESSORIAL'), titleClassNames: "text-lg font-medium leading-6 text-gray-700", show: !!accessorial && editMode, onHide: onHide, body: renderEditAccessorialRequest(), footer: renderEditAccessorialRequestFooter() })) : (React.createElement(CargomaticModal, { showCloseButton: false, title: t('ADD_ALLOWED_ACCESSORIAL'), titleClassNames: "text-lg font-medium leading-6 text-gray-700", show: addMode, onHide: onHide, body: renderEditAccessorialRequest(), footer: renderEditAccessorialRequestFooter() }));
}
